/* eslint-disable @typescript-eslint/no-explicit-any -- Work breakdown forms stitch together dynamic operational schemas, submissions, and template payloads. */
// ============================================================================
// WORK BREAKDOWN TAB — FORMS
// Forms-only work breakdown: lists the operational forms assigned to the work
// item (auto-assigned, inherited, and manually added) as appointment-style
// rows. Admins add forms via the "+ New Form" catalogue picker modal.
// Date: February 24, 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/work-breakdown.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/work-breakdown)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Plus, Loader2, Search, Shield, FileText,
  CheckCircle, Clock, Trash2, Sparkles, Pencil, Edit2,
} from 'lucide-react';
import AppointmentReferenceBadge from './AppointmentReferenceBadge';
import FormPickerModal from './FormPickerModal';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { OperationalFormModal } from '@/app/client-zone/modules/total-operations/components/smart-task/components/OperationalFormModal';
import type { OperationalForm, FormSubmission, WorkItem } from '@/app/client-zone/modules/total-operations/components/smart-task/hooks/useOperationalForms';
import { buildWorkItemPrefillValues } from '@/app/client-zone/modules/total-operations/components/smart-task/hooks/useOperationalForms';
import { isOperationalFormRequired, type OperationalFormRequirementScope } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/operationalForms';
import { isTerminalWorkItemStatus } from '@/lib/work-items/operationalStatusConfig';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';

// Lazy load the AI Forms Assistant and Unified Designer (code-split)
const AIFormsAssistant = React.lazy(() => import('@/app/client-zone/modules/total-forms/components/AIFormsAssistant'));
const UnifiedDesigner = React.lazy(() => import('@/components/designer').then((m) => ({ default: m.UnifiedDesigner })));

type DynamicFormComplianceMeta = NonNullable<OperationalForm['compliance_meta']> & {
  compliance_standard?: string;
};
type RepeatGroupCatalog = NonNullable<DynamicFormComplianceMeta['repeat_group_catalog']>;
type RepeatGroupCatalogEntry = RepeatGroupCatalog[string];

// ============================================================================
// TYPES
// ============================================================================

interface WorkBreakdownTabProps {
  workItemId: string;
  workItemType: string;
  workItemSubtype?: string | null;
  isActive: boolean;
  /** Include related work items (job children / grouped appointments) */
  includeRelated?: boolean;
  userRole?: string;
  /** When true, hides admin/AI tools and makes forms read-only (field worker SmartTask mode) */
  readOnly?: boolean;
  /** Optional forms-only lock override. When omitted, forms follow the general readOnly mode. */
  formsReadOnly?: boolean;
  onCountsChange?: (counts: { subtasks: number; checklists: number; validations: number; dynamicForms: number }) => void;
  onOpenItem?: (itemId: string) => void;
  /** Parent job ID — when set, inherited forms from the parent job are loaded (for appointments) */
  parentJobId?: string;
  /** Callback when a form is selected (for opening in OperationalFormModal) */
  onFormSelect?: (form: DynamicFormInfo, existingSubmission?: FormSubmissionInfo | null) => void;
  /** Current status of the work item — used to filter forms for terminal appointments */
  workItemStatus?: string;
  /** Increment to trigger a refresh of forms & submissions (e.g. after external form submit) */
  refreshTrigger?: number;
  /** When true, shows the AI Forms Assistant button in the forms section header.
   *  Only enabled in admin contexts (WorkItemDetailModal), NOT in technician
   *  contexts (AppointmentDetailPanel). */
  enableAiAssistant?: boolean;
  /** ISO timestamp when the appointment was completed/resolved. Used to check
   *  whether forms with `compliance_meta.editable_window_days` are still
   *  editable by the technician after the appointment ends. */
  appointmentCompletedAt?: string | null;
  /** ISO timestamp when the parent job was closed. When null, the job is still
   *  open and the editable window is still active (if within the day limit). */
  jobClosedAt?: string | null;
  /** When true, the component operates in technician mode: it skips loading
   *  the full client form catalogue (which is slow), only fetches the forms
   *  assigned to this work item + submissions, filters to required/started
   *  forms, and never shows add-form UI. Used by AppointmentDetailPanel where
   *  the technician only needs to see and complete their assigned forms. */
  technicianMode?: boolean;
}

/** Info about sibling appointment form submissions */
interface SiblingSubmissionInfo {
  submission_id: number;
  appointment_id: string | number;
  appointment_number: string;
  appointment_title?: string;
  status: string;
  submitted_at: string;
  submitted_by_name: string;
  is_current_appointment: boolean;
}

export interface DynamicFormInfo {
  id: number;
  form_key: string;
  title: string;
  description?: string;
  category?: string;
  applies_to_item_type?: string;
  applies_to_subtype?: string;
  icon?: string;
  color?: string;
  is_system?: boolean;
  compliance_meta?: DynamicFormComplianceMeta;
  form_schema?: OperationalForm['form_schema'];
  tags?: string[];
  required_for_job?: boolean;
  required_for_appointment?: boolean;
  source_form_id?: number | null;
  assignment_scope?: OperationalForm['assignment_scope'];
  generated_for_work_item_id?: OperationalForm['generated_for_work_item_id'];
  generated_from_submission_id?: OperationalForm['generated_from_submission_id'];
  assignment_source?: 'auto' | 'extra' | 'inherited' | 'submission' | 'site';
  assignment_source_work_item_id?: string | number | null;
  /** Builder/wizard visual theme config. */
  theme_config?: Record<string, unknown> | null;
  /** Builder/wizard branding config (logo, watermark). */
  branding_config?: Record<string, unknown> | null;
  /** Success page message shown after submission. */
  success_message?: string | null;
  /** Optional redirect URL after submission. */
  success_redirect_url?: string | null;
  /** Optional image to display on the success screen. */
  success_image_url?: string | null;
  /** Delay in seconds before auto-redirecting (0 = no redirect). */
  success_redirect_delay?: number | null;
  /** Whether to show a summary of submitted data on the success screen. */
  success_show_summary?: boolean | null;
  /** Whether the respondent may submit another response. */
  success_allow_another?: boolean | null;
  /** Whether to show the progress bar / ring. */
  show_progress_bar?: boolean | null;
}

type FormAssignmentRequirement = {
  required_for_job: boolean;
  required_for_appointment: boolean;
};

interface FormSubmissionInfo {
  id: number;
  form_key: string;
  form_title?: string;
  status: 'draft' | 'pass' | 'fail' | 'conditional' | 'submitted';
  submitted_at?: string;
  submitted_by_name?: string;
  work_item_id: string;
  form_data?: Record<string, unknown>;
  section_results?: FormSubmission['section_results'];
  overall_status?: string;
  completion_percentage?: number;
  /** Set when this submission came from a child appointment (job view) */
  source_appointment_number?: string;
  source_appointment_title?: string;
  is_child_submission?: boolean;
}

const normalizeSubmissionWorkItemId = (value: string | number | null | undefined) => String(value || '');

const DEFAULT_FORM_REQUIREMENT: FormAssignmentRequirement = {
  required_for_job: false,
  required_for_appointment: false,
};

function getRepeatGroupCatalog(form: Pick<DynamicFormInfo, 'compliance_meta'> | null | undefined): RepeatGroupCatalog {
  const catalog = form?.compliance_meta?.repeat_group_catalog;
  return catalog && typeof catalog === 'object' ? catalog : {};
}

function getGeneratedVariantRepeatCounts(form: DynamicFormInfo | null | undefined): Record<string, number> {
  const rawCounts = form?.compliance_meta?.generated_variant?.repeat_group_counts
    ?? (form?.form_schema as Record<string, any> | undefined)?.generation_meta?.repeat_group_counts
    ?? {};

  if (!rawCounts || typeof rawCounts !== 'object' || Array.isArray(rawCounts)) {
    return {};
  }

  return Object.entries(rawCounts as Record<string, unknown>).reduce<Record<string, number>>((acc, [key, value]) => {
    const normalized = Number(value);
    if (Number.isFinite(normalized) && normalized > 0) {
      acc[key] = Math.floor(normalized);
    }
    return acc;
  }, {});
}

function isGeneratedOnceOffForm(form: Pick<DynamicFormInfo, 'assignment_scope'> | null | undefined): boolean {
  return form?.assignment_scope === 'work_item_once_off';
}

function mergeFormsByFormKey<TForm extends {
  id: number;
  form_key: string;
  required_for_job?: boolean | null;
  required_for_appointment?: boolean | null;
}>(
  directForms: TForm[],
  inheritedForms: TForm[],
): TForm[] {
  const mergedForms = [...directForms];
  const indexByFormKey = new Map<string, number>();

  mergedForms.forEach((form, index) => {
    indexByFormKey.set(form.form_key || `id:${form.id}`, index);
  });

  inheritedForms.forEach((form) => {
    const formKey = form.form_key || `id:${form.id}`;
    const existingIndex = indexByFormKey.get(formKey);

    if (existingIndex == null) {
      indexByFormKey.set(formKey, mergedForms.length);
      mergedForms.push(form);
      return;
    }

    const existingForm = mergedForms[existingIndex];
    mergedForms[existingIndex] = {
      ...existingForm,
      ...form,
      required_for_job: Boolean(existingForm.required_for_job || form.required_for_job),
      required_for_appointment: Boolean(existingForm.required_for_appointment || form.required_for_appointment),
    };
  });

  return mergedForms;
}

function toNumericWorkItemId(value: string): number | undefined {
  return /^\d+$/.test(value) ? Number(value) : undefined;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function WorkBreakdownTab({
  workItemId,
  workItemType,
  workItemSubtype,
  isActive,
  includeRelated = false,
  userRole = 'user',
  readOnly = false,
  formsReadOnly,
  onCountsChange,
  onOpenItem,
  parentJobId,
  onFormSelect,
  workItemStatus,
  refreshTrigger,
  enableAiAssistant = false,
  appointmentCompletedAt,
  jobClosedAt,
  technicianMode = false,
}: WorkBreakdownTabProps) {
  const [dynamicFormsCount, setDynamicFormsCount] = useState(0);

  // Card-level search — filters the rendered forms list.
  const [contentSearch, setContentSearch] = useState('');

  // Dynamic Forms (from form_configurations)
  const [dynamicForms, setDynamicForms] = useState<DynamicFormInfo[]>([]);
  const [formSubmissions, setFormSubmissions] = useState<FormSubmissionInfo[]>([]);
  const [loadingDynamicForms, setLoadingDynamicForms] = useState(false);

  // Form assignment management (admin feature)
  const [assignedFormIds, setAssignedFormIds] = useState<number[]>([]);
  const [allActiveClientForms, setAllActiveClientForms] = useState<DynamicFormInfo[]>([]);
  const [showFormPicker, setShowFormPicker] = useState(false);
  const [savingFormAssignment, setSavingFormAssignment] = useState(false);
  // AI Forms Assistant (admin-only, floating dockable panel)
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  // Form Configuration Editor (opened from edit button on form rows)
  const [formBuilderOpen, setFormBuilderOpen] = useState(false);
  const [editingFormKey, setEditingFormKey] = useState<string | undefined>(undefined);
  // Track auto-assigned form IDs (from job type)
  const [autoAssignedFormIds, setAutoAssignedFormIds] = useState<number[]>([]);
  // Track admin-added extras and admin-removed auto forms separately
  const [extraFormIds, setExtraFormIds] = useState<number[]>([]);
  const [removedAutoFormIds, setRemovedAutoFormIds] = useState<number[]>([]);
  const [assignmentRequirements, setAssignmentRequirements] = useState<Record<string, FormAssignmentRequirement>>({});
  // Stable ref so loadInheritedForms can read current value without being in its deps
  const removedAutoFormIdsRef = useRef<number[]>([]);
  removedAutoFormIdsRef.current = removedAutoFormIds;
  const extraFormIdsRef = useRef<number[]>([]);
  extraFormIdsRef.current = extraFormIds;
  const assignmentRequirementsRef = useRef<Record<string, FormAssignmentRequirement>>({});
  assignmentRequirementsRef.current = assignmentRequirements;

  // Inherited forms from parent job (for appointments)
  const [inheritedForms, setInheritedForms] = useState<DynamicFormInfo[]>([]);
  // Track inherited form IDs so handleToggleForm can recognise them
  const [inheritedFormIds, setInheritedFormIds] = useState<number[]>([]);
  const [siblingSubmissions, setSiblingSubmissions] = useState<Record<string, SiblingSubmissionInfo[]>>({});
  const [_parentJobNumber, setParentJobNumber] = useState<string>('');
  const [loadingInherited, setLoadingInherited] = useState(false);

  // Built-in form modal state (when no external onFormSelect is provided)
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<OperationalForm | null>(null);
  const [selectedFormSubmission, setSelectedFormSubmission] = useState<FormSubmission | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // Work item context for linked-field prefill — this tab only receives a
  // workItemId, so the item is fetched once per work item (eagerly, so the
  // prefill is usually already resolved before a form modal opens — a late
  // initialData change would re-run the modal's init effect and could wipe
  // in-progress edits). Reuses the same mapping as the appointment panel's
  // getPrefilledValues (single source: buildWorkItemPrefillValues).
  const [prefillWorkItem, setPrefillWorkItem] = useState<WorkItem | null>(null);
  const prefillFetchedForRef = useRef<string | null>(null);

  const getAuthToken = useCallback(() => authService.getTokens()?.access_token || '', []);

  useEffect(() => {
    if (!workItemId || prefillFetchedForRef.current === workItemId) return;
    prefillFetchedForRef.current = workItemId;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/v1/work-items/${workItemId}?lite=1`), {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!res.ok || cancelled) return;
        const item = (await res.json())?.data;
        if (item && !cancelled) setPrefillWorkItem(item as WorkItem);
      } catch {
        // Prefill stays user/date-only when the work item can't be loaded
      }
    })();
    return () => { cancelled = true; };
  }, [workItemId, getAuthToken]);

  // Memoize prefill values for the built-in OperationalFormModal so the init effect
  // doesn't re-run on every parent render and wipe user edits
  const builtInModalPrefill = useMemo(() => {
    if (!selectedForm) return {};
    const currentUser = authService.getUser();
    const userFullName = currentUser
      ? (currentUser.display_name || `${currentUser.first_name} ${currentUser.last_name}`.trim())
      : undefined;
    const today = new Date().toISOString().split('T')[0];
    const values: Record<string, unknown> = {
      technician_name: userFullName,
      user_name: userFullName,
      inspector_name: userFullName,
      completed_by: userFullName,
      // Common aliases for user / technician / contractor fields
      contractor_name: userFullName,
      inspection_done_by: userFullName,
      inspected_by: userFullName,
      performed_by: userFullName,
      completed_by_name: userFullName,
      user_id: currentUser?.id,
      technician_id: currentUser?.id,
      inspector_id: currentUser?.id,
      contractor_id: currentUser?.id,
      user_email: currentUser?.email,
      technician_email: currentUser?.email,
      contractor_email: currentUser?.email,
      inspection_date: today,
      completion_date: today,
      current_date: today,
      date: today,
      inspected_date: today,
      performed_date: today,
      work_item_id: workItemId,
    };
    if (selectedForm.form_schema?.sections) {
      const fieldKeys = new Set<string>();
      for (const section of selectedForm.form_schema.sections) {
        for (const field of section.fields) {
          fieldKeys.add(field.key);
        }
      }
      const filtered: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(values)) {
        if (fieldKeys.has(key) && value !== undefined) {
          filtered[key] = value;
        }
      }
      // Full work-item mapping (job_number, site_*, customer_*, scheduled_*,
      // metadata/custom_fields matches) once the item has loaded.
      return { ...filtered, ...buildWorkItemPrefillValues(prefillWorkItem, selectedForm) };
    }
    return {};
  }, [selectedForm, workItemId, prefillWorkItem]);

  const isAdmin = userRole === 'admin' || userRole === 'owner' || userRole === 'root';
  const requirementScope: OperationalFormRequirementScope = workItemType === 'appointment' ? 'appointment' : 'job';
  const effectiveFormsReadOnly = formsReadOnly ?? readOnly;
  const canManageForms = isAdmin && !readOnly && !effectiveFormsReadOnly && !technicianMode;

  // ──────────────────────────────────────────────────────────────────────
  // Editable window: check if a form is still editable by the technician
  // after the appointment ends. The form's compliance_meta.editable_window_days
  // specifies how many days after appointment completion the form remains
  // editable. The window also closes when the parent job is closed.
  // Callers that don't pass the timestamps get them resolved from the work item
  // record (resolution columns first, then custom_fields fallbacks).
  // ──────────────────────────────────────────────────────────────────────
  const [fallbackAppointmentCompletedAt, setFallbackAppointmentCompletedAt] = useState<string | null>(null);
  const [fallbackJobClosedAt, setFallbackJobClosedAt] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch if at least one prop is undefined — don't overwrite a
    // supplied prop's fallback when only the other is undefined.
    const needAppointmentCompleted = appointmentCompletedAt === undefined;
    const needJobClosed = jobClosedAt === undefined;
    if (!needAppointmentCompleted && !needJobClosed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl(`/api/v1/work-items/${workItemId}`), {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!res.ok) return;
        const item = (await res.json())?.data;
        if (!item || cancelled) return;
        const cf = typeof item.custom_fields === 'string'
          ? (() => { try { return JSON.parse(item.custom_fields); } catch { return null; } })()
          : item.custom_fields;
        const cfTimestamp = (key: string) => (cf && typeof cf[key] === 'string' ? cf[key] as string : null);
        if (needAppointmentCompleted && !cancelled) {
          // Match the server's anchor order in submissionEditWindow.ts:
          // resolved_at → completed_at → closed_at → cf.check_out_timestamp → cf.resolved_at
          setFallbackAppointmentCompletedAt(
            item.resolved_at || item.completed_at || item.closed_at || cfTimestamp('check_out_timestamp') || cfTimestamp('resolved_at') || null,
          );
        }
        if (needJobClosed && !cancelled) {
          if (workItemType === 'job') {
            setFallbackJobClosedAt(
              item.resolved_at || item.completed_at || item.closed_at || cfTimestamp('resolved_at') || null,
            );
          } else if (workItemType === 'appointment' && item.parent_id) {
            try {
              const parentRes = await fetch(getApiUrl(`/api/v1/work-items/${item.parent_id}`), {
                headers: { Authorization: `Bearer ${getAuthToken()}` },
              });
              if (parentRes.ok && !cancelled) {
                const parentItem = (await parentRes.json())?.data;
                if (parentItem) {
                  const parentCf = typeof parentItem.custom_fields === 'string'
                    ? (() => { try { return JSON.parse(parentItem.custom_fields); } catch { return null; } })()
                    : parentItem.custom_fields;
                  const parentCfTimestamp = (key: string) => (parentCf && typeof parentCf[key] === 'string' ? parentCf[key] as string : null);
                  if (!cancelled) {
                    setFallbackJobClosedAt(
                      parentItem.resolved_at || parentItem.completed_at || parentItem.closed_at || parentCfTimestamp('resolved_at') || null,
                    );
                  }
                }
              }
            } catch {
              // Parent close status unknown; leave job closed at null
            }
          }
        }
      } catch {
        // Editable window just won't engage for callers that don't pass timestamps
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentCompletedAt, jobClosedAt, workItemId, workItemType, getAuthToken]);

  const effectiveAppointmentCompletedAt = appointmentCompletedAt ?? fallbackAppointmentCompletedAt;
  const effectiveJobClosedAt = jobClosedAt ?? fallbackJobClosedAt;

  const isFormWithinEditableWindow = useCallback((form: DynamicFormInfo): boolean => {
    if (!effectiveFormsReadOnly) return true; // forms are already editable

    // Whether the work item has actually ended (resolved/closed/completed/…).
    // The editable window only applies AFTER the item ends — on scheduled or
    // open items the forms lock still applies regardless of window config.
    const workItemEnded = isTerminalWorkItemStatus(workItemStatus);

    // Admin-tier roles are exempt from the editable window — the API guard
    // (submissionEditWindow.ts) skips them entirely, so lock-step the UI.
    // Only applied when the dedicated forms lock (formsReadOnly) is the gate:
    // a plain `readOnly` (view-only embedding) still applies to everyone.
    if (isAdmin && formsReadOnly === true && workItemEnded) return true;

    // Forms are locked. Check if this specific form has an editable window.
    const windowDays = form.compliance_meta?.editable_window_days;
    if (!windowDays || windowDays <= 0) return false;
    // Job closed → window is over
    if (effectiveJobClosedAt) return false;
    // No completion timestamp: the window only engages once the item has
    // ended. A resolved item missing its timestamp stays editable (the server
    // finds no resolvedAt and allows too); a scheduled/open item stays locked.
    if (!effectiveAppointmentCompletedAt) return workItemEnded;
    // Unparseable timestamp → same rule: editable only once the item ended.
    const completedMs = new Date(effectiveAppointmentCompletedAt).getTime();
    if (!Number.isFinite(completedMs)) return workItemEnded;
    const elapsedMs = Date.now() - completedMs;
    const windowMs = windowDays * 24 * 60 * 60 * 1000;
    return elapsedMs <= windowMs;
  }, [effectiveFormsReadOnly, formsReadOnly, isAdmin, workItemStatus, effectiveAppointmentCompletedAt, effectiveJobClosedAt]);

  // State for the editable window inline editor (admin-only)
  const [editingWindowFormId, setEditingWindowFormId] = useState<number | null>(null);
  const [windowDaysInput, setWindowDaysInput] = useState<string>('');
  const [savingWindowDays, setSavingWindowDays] = useState(false);

  const handleSaveEditableWindow = useCallback(async (form: DynamicFormInfo) => {
    const days = parseInt(windowDaysInput, 10);
    if (!Number.isFinite(days) || days < 0) {
      setEditingWindowFormId(null);
      return;
    }
    setSavingWindowDays(true);
    try {
      const token = getAuthToken();
      const currentMeta = (form.compliance_meta || {}) as Record<string, unknown>;
      const updatedMeta = { ...currentMeta, editable_window_days: days > 0 ? days : null };
      const res = await fetch(getApiUrl(`/api/v1/forms/operational/${form.form_key}`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ compliance_meta: updatedMeta }),
      });
      if (res.ok) {
        const data = await res.json();
        const newMeta = data.data?.compliance_meta;
        // Update local state
        setDynamicForms((prev) => prev.map((f) => (
          f.id === form.id ? { ...f, compliance_meta: newMeta as DynamicFormComplianceMeta } : f
        )));
        setAllActiveClientForms((prev) => prev.map((f) => (
          f.id === form.id ? { ...f, compliance_meta: newMeta as DynamicFormComplianceMeta } : f
        )));
        setInheritedForms((prev) => prev.map((f) => (
          f.id === form.id ? { ...f, compliance_meta: newMeta as DynamicFormComplianceMeta } : f
        )));
      }
    } catch (error) {
      console.error('[WorkBreakdownTab] Error saving editable window:', error);
    } finally {
      setSavingWindowDays(false);
      setEditingWindowFormId(null);
    }
  }, [getAuthToken, windowDaysInput]);

  const getRequirementForForm = useCallback((
    formId: number | string | null | undefined,
    fallback?: Partial<FormAssignmentRequirement> | null,
  ) => {
    const explicitRequirement = formId != null
      ? assignmentRequirementsRef.current[String(formId)]
      : undefined;

    return {
      required_for_job: Boolean(explicitRequirement?.required_for_job ?? fallback?.required_for_job),
      required_for_appointment: Boolean(explicitRequirement?.required_for_appointment ?? fallback?.required_for_appointment),
    };
  }, []);

  const applyRequirementToForm = useCallback((form: DynamicFormInfo): DynamicFormInfo => {
    const requirement = getRequirementForForm(form.id, form);
    return {
      ...form,
      required_for_job: requirement.required_for_job,
      required_for_appointment: requirement.required_for_appointment,
    };
  }, [getRequirementForForm]);

  const openFormsGenerator = useCallback((form?: DynamicFormInfo | null) => {
    const params = new URLSearchParams({
      tab: 'dashboard',
      generate_operational_variant: 'true',
      work_item_id: workItemId,
    });

    if (form?.id != null) {
      params.set('source_form_id', String(form.id));
    }

    if (form?.form_key) {
      params.set('source_form_key', form.form_key);
    }

    if (typeof window !== 'undefined') {
      window.location.href = `/client-zone/modules/total-forms?${params.toString()}`;
    }
  }, [workItemId]);

  const persistFormAssignments = useCallback(async (
    nextExtraFormIds: number[],
    nextRemovedAutoFormIds: number[],
    nextAssignmentRequirements: Record<string, FormAssignmentRequirement>,
  ) => {
    const res = await fetch(getApiUrl(`/api/v1/work-items/forms`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        work_item_id: workItemId,
        extra_form_ids: nextExtraFormIds,
        removed_auto_form_ids: nextRemovedAutoFormIds,
        assignment_requirements: nextAssignmentRequirements,
        include_related: includeRelated,
      }),
    });
    if (!res.ok) {
      console.warn('[WorkBreakdownTab] persistFormAssignments failed:', res.status, await res.text().catch(() => ''));
    }
  }, [includeRelated, workItemId, getAuthToken]);

  // ──────────────────────────────────────────────────────────────────────
  // Load dynamic forms (from form_configurations) + submissions + assignments
  // ──────────────────────────────────────────────────────────────────────
  const loadDynamicForms = useCallback(async () => {
    setLoadingDynamicForms(true);
    try {
      const token = getAuthToken();
      // Load: operational forms for this item type/subtype, submissions, and form assignments
      const params = new URLSearchParams({ item_type: workItemType });
      if (workItemSubtype) {
        params.set('subtype', workItemSubtype);
        params.set('job_type', workItemSubtype);
      }
      params.set('work_item_id', workItemId);
      // client_only=true excludes catalogue-only system forms that haven't been
      // enabled (copied with a client_id) for this organisation
      params.set('client_only', 'true');
      if (includeRelated) {
        params.set('include_related', 'true');
      }

      if (technicianMode) {
        // ── Technician mode (AppointmentDetailPanel) ──
        // Skip the expensive full-catalogue fetches. Only load:
        //  1. Submissions for this work item (to show what's been started)
        //  2. Form assignments (without all_active_forms) to know which forms
        //     are assigned to this work item
        // The operational forms list is still needed to render the assigned
        // forms, but we can skip all_active_forms (the full client catalogue)
        // since the technician cannot add forms.
        const [formsRes, subsRes, assignRes] = await Promise.all([
          fetch(getApiUrl(`/api/v1/forms/operational?${params.toString()}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(getApiUrl(`/api/v1/forms/submissions?work_item_id=${workItemId}&include_children=true${includeRelated ? '&include_related=true' : ''}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          // No client_only=true — we don't need all_active_forms in technician mode.
          // The backend still returns extra_form_ids, removed_auto_form_ids,
          // and assignment_requirements without the full catalogue.
          fetch(getApiUrl(`/api/v1/work-items/forms?work_item_id=${workItemId}&assigned_only=true${includeRelated ? '&include_related=true' : ''}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        let directForms: DynamicFormInfo[] = [];

        if (formsRes.ok) {
          const formsData = await formsRes.json();
          if (formsData.success) {
            directForms = formsData.data || [];
          }
        }

        if (subsRes.ok) {
          const subsData = await subsRes.json();
          if (subsData.success) {
            setFormSubmissions(subsData.data || []);
          }
        }

        let savedExtraIds: number[] = [];
        let savedRemovedAutoIds: number[] = [];
        let savedAssignmentRequirements: Record<string, FormAssignmentRequirement> = {};

        if (assignRes.ok) {
          const assignData = await assignRes.json();
          if (assignData.success && assignData.data) {
            savedExtraIds = assignData.data.extra_form_ids || [];
            savedRemovedAutoIds = assignData.data.removed_auto_form_ids || [];
            savedAssignmentRequirements = assignData.data.assignment_requirements || {};
            // In technician mode, all_active_forms may be absent or empty.
            // We don't need it — the technician can't add forms.
          }
        }

        setAssignmentRequirements(savedAssignmentRequirements);

        const enrichedDirectForms = directForms.map((form) => ({
          ...form,
          ...(savedAssignmentRequirements[String(form.id)] || DEFAULT_FORM_REQUIREMENT),
        }));

        // Track auto-assigned form IDs
        const autoIds = enrichedDirectForms
          .filter((form) => form.assignment_source === 'auto')
          .map((form) => form.id);
        setAutoAssignedFormIds(autoIds);
        // No allActiveClientForms in technician mode — leave it empty
        setAllActiveClientForms([]);
        setExtraFormIds(savedExtraIds);
        setRemovedAutoFormIds(savedRemovedAutoIds);
        setAssignedFormIds(enrichedDirectForms.map((form) => form.id));
        setDynamicForms(enrichedDirectForms);
        setDynamicFormsCount(enrichedDirectForms.length);
      } else {
        // ── Admin mode (WorkItemDetailModal) ──
        // Full load: operational forms, submissions, and the complete client
        // form catalogue (needed for the add-form picker).
        const [formsRes, subsRes, assignRes] = await Promise.all([
          fetch(getApiUrl(`/api/v1/forms/operational?${params.toString()}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(getApiUrl(`/api/v1/forms/submissions?work_item_id=${workItemId}&include_children=true${includeRelated ? '&include_related=true' : ''}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(getApiUrl(`/api/v1/work-items/forms?work_item_id=${workItemId}&client_only=true${includeRelated ? '&include_related=true' : ''}`), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        let directForms: DynamicFormInfo[] = [];
        let allActiveForms: DynamicFormInfo[] = [];

        if (formsRes.ok) {
          const formsData = await formsRes.json();
          if (formsData.success) {
            directForms = formsData.data || [];
          }
        }

        if (subsRes.ok) {
          const subsData = await subsRes.json();
          if (subsData.success) {
            setFormSubmissions(subsData.data || []);
          }
        }

        let savedExtraIds: number[] = [];
        let savedRemovedAutoIds: number[] = [];
        let savedAssignmentRequirements: Record<string, FormAssignmentRequirement> = {};

        if (assignRes.ok) {
          const assignData = await assignRes.json();
          if (assignData.success && assignData.data) {
            savedExtraIds = assignData.data.extra_form_ids || [];
            savedRemovedAutoIds = assignData.data.removed_auto_form_ids || [];
            savedAssignmentRequirements = assignData.data.assignment_requirements || {};
            allActiveForms = assignData.data.all_active_forms || [];
          }
        }

        setAssignmentRequirements(savedAssignmentRequirements);

        const enrichedDirectForms = directForms.map((form) => ({
          ...form,
          ...(savedAssignmentRequirements[String(form.id)] || DEFAULT_FORM_REQUIREMENT),
        }));
        const enrichedAllActiveForms = allActiveForms.map((form) => ({
          ...form,
          ...(savedAssignmentRequirements[String(form.id)] || DEFAULT_FORM_REQUIREMENT),
        }));

        // Track auto-assigned form IDs
        const autoIds = enrichedDirectForms
          .filter((form) => form.assignment_source === 'auto')
          .map((form) => form.id);
        setAutoAssignedFormIds(autoIds);
        setAllActiveClientForms(enrichedAllActiveForms);
        setExtraFormIds(savedExtraIds);
        setRemovedAutoFormIds(savedRemovedAutoIds);
        setAssignedFormIds(enrichedDirectForms.map((form) => form.id));
        setDynamicForms(enrichedDirectForms);
        setDynamicFormsCount(enrichedDirectForms.length);
      }
    } catch (error) {
      console.error('Error loading dynamic forms:', error);
    } finally {
      setLoadingDynamicForms(false);
    }
  }, [includeRelated, workItemId, workItemType, workItemSubtype, getAuthToken, technicianMode]);

  // ──────────────────────────────────────────────────────────────────────
  // Load inherited forms from parent job (for appointments)
  // ──────────────────────────────────────────────────────────────────────
  const loadInheritedForms = useCallback(async () => {
    // Resolve parentJobId: use prop if provided, otherwise auto-resolve for appointments
    let resolvedParentJobId = parentJobId;
    if (!resolvedParentJobId && workItemType === 'appointment') {
      try {
        const token = getAuthToken();
        const itemRes = await fetch(getApiUrl(`/api/v1/work-items/${workItemId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (itemRes.ok) {
          const itemData = await itemRes.json();
          resolvedParentJobId = itemData.data?.parent_id
            || itemData.data?.relationships?.parent
            || null;
        }
      } catch (err) {
        console.error('[WorkBreakdown] Failed to resolve parent for appointment:', err);
      }
    }
    if (!resolvedParentJobId) return;
    setLoadingInherited(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams({
        parent_job_id: resolvedParentJobId,
        appointment_id: workItemId,
      });
      const res = await fetch(getApiUrl(`/api/v1/work-items/forms/inherited?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          const inherited = (data.data.inherited_forms || []) as DynamicFormInfo[];
          const enrichedInherited = inherited.map(applyRequirementToForm);
          setInheritedForms(enrichedInherited);
          setInheritedFormIds(enrichedInherited.map(f => f.id));
          setSiblingSubmissions(data.data.sibling_submissions || {});
          setParentJobNumber(data.data.parent_job_number || '');

          // Merge inherited forms into the dynamic forms list
          // (only add forms not already assigned directly to this appointment
          //  AND not in the appointment's own removed_auto_form_ids)
          setDynamicForms(prev => {
            // Read the appointment's current removedAutoFormIds via ref to honour local removals
            const localRemoved = new Set(removedAutoFormIdsRef.current);
            const visibleInherited = enrichedInherited.filter(f => !localRemoved.has(f.id));
            const merged = mergeFormsByFormKey(prev, visibleInherited);
            setDynamicFormsCount(merged.length);
            return merged;
          });
        }
      }
    } catch (error) {
      console.error('Error loading inherited forms:', error);
    } finally {
      setLoadingInherited(false);
    }
  }, [parentJobId, workItemId, workItemType, applyRequirementToForm, getAuthToken]);

  // ──────────────────────────────────────────────────────────────────────
  // AI assistant: link a newly created form to this work item
  // Called when the AI Forms Assistant creates a once-off form and we need
  // to assign it as an extra form on the current job/appointment.
  // ──────────────────────────────────────────────────────────────────────
  const linkAiCreatedFormToWorkItem = useCallback(async (formKey: string) => {
    try {
      // Reload the forms list to pick up the newly created form
      await loadDynamicForms();
      if (parentJobId || workItemType === 'appointment') {
        await loadInheritedForms();
      }

      // Find the form by key
      const token = getAuthToken();
      const res = await fetch(getApiUrl(`/api/v1/forms/operational/${formKey}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !data.data?.id) return;

      const newFormId = data.data.id as number;

      // Add it as an extra form assignment (don't remove auto forms)
      const currentExtras = extraFormIdsRef.current;
      if (currentExtras.includes(newFormId)) return; // already linked

      const nextExtras = [...currentExtras, newFormId];
      setExtraFormIds(nextExtras);
      setAssignedFormIds((prev) => [...prev, newFormId]);

      await persistFormAssignments(
        nextExtras,
        removedAutoFormIdsRef.current,
        assignmentRequirementsRef.current,
      );
    } catch (error) {
      console.error('[WorkBreakdownTab] Error linking AI-created form:', error);
    }
  }, [loadDynamicForms, loadInheritedForms, parentJobId, workItemType, getAuthToken, persistFormAssignments]);

  useEffect(() => {
    if (isActive) {
      // Load inherited forms for appointments — either from explicit parentJobId
      // or by auto-resolving the parent from the appointment's relationships
      if (parentJobId || workItemType === 'appointment') {
        // Load direct forms first, then merge inherited on top
        loadDynamicForms().then(() => loadInheritedForms());
      } else {
        loadDynamicForms();
      }
    }
  }, [isActive, loadDynamicForms, parentJobId, workItemType, loadInheritedForms]);

  // ── Refresh when external trigger changes (e.g. after form submission in SmartTask) ──
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      if (parentJobId || workItemType === 'appointment') {
        loadDynamicForms().then(() => loadInheritedForms());
      } else {
        loadDynamicForms();
      }
    }
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Terminal appointment filtering ──────────────────────────────────
  // When an appointment reaches a terminal status (missed/resolved/closed/cancelled/completed),
  // show all REQUIRED forms (even if unstarted — the technician needs to see
  // what was missed) plus any forms that have at least one submission.
  // Previously this hid ALL unstarted forms from non-admins, which masked
  // missing required forms — the office only found out via gates.
  // Jobs always show ALL forms regardless of completion.
  const isTerminalAppointment = useMemo(() => {
    return workItemType === 'appointment' && isTerminalWorkItemStatus(workItemStatus);
  }, [workItemType, workItemStatus]);

  const shouldRestrictTerminalAppointmentForms = useMemo(() => {
    return isTerminalAppointment && !isAdmin;
  }, [isAdmin, isTerminalAppointment]);

  // In technician mode, only show forms the technician must complete:
  // required forms (even unstarted) + any form with a submission (started).
  // Optional forms that were never started are hidden to reduce clutter.
  const shouldFilterTechnicianForms = technicianMode || shouldRestrictTerminalAppointmentForms;

  const displayableForms = useMemo(() => {
    if (!shouldFilterTechnicianForms) return dynamicForms;
    // Show required forms (even unstarted) + any form with a submission.
    // Non-required forms that were never started are hidden to reduce clutter.
    return dynamicForms.filter(form => {
      if (formSubmissions.some(s => s.form_key === form.form_key)) return true;
      return isOperationalFormRequired(form, requirementScope);
    });
  }, [shouldFilterTechnicianForms, dynamicForms, formSubmissions, requirementScope]);

  // Use filtered count for terminal appointments or technician mode
  const effectiveFormsCount = shouldFilterTechnicianForms ? displayableForms.length : dynamicFormsCount;

  // Notify parent of count changes (subtasks/checklists/validations moved out — always 0 here)
  useEffect(() => {
    onCountsChange?.({ subtasks: 0, checklists: 0, validations: 0, dynamicForms: effectiveFormsCount });
  }, [effectiveFormsCount, onCountsChange]);

  // ──────────────────────────────────────────────────────────────────────
  // Built-in form handlers (when no external onFormSelect is provided)
  // ──────────────────────────────────────────────────────────────────────
  const handleInternalFormSelect = useCallback(async (form: DynamicFormInfo, existingSubmission?: FormSubmissionInfo | null) => {
    // If the parent provides onFormSelect, delegate to it
    if (onFormSelect) {
      onFormSelect(form, existingSubmission);
      return;
    }

    // If form_schema is missing or has no sections (e.g. form came from
    // allActiveClientForms which doesn't include form_schema, or a child
    // appointment submitted for a form the job doesn't auto-match), fetch
    // the full form config from the API so the modal can render correctly.
    let resolvedSchema = form.form_schema;
    if (!resolvedSchema?.sections?.length) {
      try {
        const token = getAuthToken();
        const schemaRes = await fetch(getApiUrl(`/api/v1/forms/operational/${encodeURIComponent(form.form_key)}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (schemaRes.ok) {
          const schemaData = await schemaRes.json();
          if (schemaData.success && schemaData.data?.form_schema) {
            resolvedSchema = schemaData.data.form_schema;
          }
        }
      } catch (err) {
        if (!isSilentFetchError(err)) console.error('[WorkBreakdown] Failed to fetch form schema for', form.form_key, err);
      }
    }

    const targetWorkItemId = existingSubmission?.work_item_id || workItemId;

    // Convert DynamicFormInfo to OperationalForm shape for the modal
    const operationalForm: OperationalForm = {
      id: form.id,
      form_key: form.form_key,
      entity_type: form.applies_to_item_type || 'work_item',
      title: form.title,
      description: form.description || '',
      form_schema: resolvedSchema || { sections: [] },
      form_type: 'operational',
      category: form.category,
      applies_to_item_type: form.applies_to_item_type,
      applies_to_subtype: form.applies_to_subtype,
      icon: form.icon,
      color: form.color,
      is_system: form.is_system || false,
      enabled: true,
      sort_order: 0,
      compliance_meta: form.compliance_meta,
      source_form_id: form.source_form_id,
      assignment_scope: form.assignment_scope,
      generated_for_work_item_id: form.generated_for_work_item_id,
      generated_from_submission_id: form.generated_from_submission_id,
      tags: form.tags,
      version: 1,
      theme_config: form.theme_config,
      branding_config: form.branding_config,
      success_message: form.success_message,
      success_redirect_url: form.success_redirect_url,
      success_image_url: form.success_image_url,
      success_redirect_delay: form.success_redirect_delay,
      success_show_summary: form.success_show_summary,
      success_allow_another: form.success_allow_another,
      show_progress_bar: form.show_progress_bar,
    };

    setSelectedForm(operationalForm);

    // Convert FormSubmissionInfo to FormSubmission if exists
    if (existingSubmission) {
      // If we already have form_data from the list endpoint, use it directly
      const hasFormData = existingSubmission.form_data && Object.keys(existingSubmission.form_data).length > 0;

      if (hasFormData) {
        setSelectedFormSubmission({
          id: existingSubmission.id,
          form_configuration_id: form.id,
          form_key: existingSubmission.form_key,
          work_item_id: toNumericWorkItemId(String(targetWorkItemId)),
          entity_type: form.applies_to_item_type || 'work_item',
          client_id: 0,
          submitted_by: 0,
          form_data: existingSubmission.form_data,
          section_results: existingSubmission.section_results,
          overall_status: (existingSubmission.overall_status || existingSubmission.status) as FormSubmission['overall_status'],
          completion_percentage: existingSubmission.completion_percentage ?? (existingSubmission.status === 'pass' || existingSubmission.status === 'submitted' ? 100 : 0),
          submitted_at: existingSubmission.submitted_at,
          created_at: existingSubmission.submitted_at || '',
          updated_at: existingSubmission.submitted_at || '',
        } as FormSubmission);
        setShowFormModal(true);
      } else {
        // Need to fetch full submission data from the API
        setShowFormModal(true);
        const token = getAuthToken();
        fetch(getApiUrl(`/api/v1/forms/submissions/${existingSubmission.id}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.success && data.data) {
              setSelectedFormSubmission(data.data);
            } else {
              // Fallback: set stub so modal at least opens
              setSelectedFormSubmission({
                id: existingSubmission.id,
                form_configuration_id: form.id,
                form_key: existingSubmission.form_key,
                work_item_id: toNumericWorkItemId(String(targetWorkItemId)),
                entity_type: form.applies_to_item_type || 'work_item',
                client_id: 0,
                submitted_by: 0,
                form_data: {},
                overall_status: existingSubmission.status as FormSubmission['overall_status'],
                completion_percentage: 0,
                submitted_at: existingSubmission.submitted_at,
                created_at: existingSubmission.submitted_at || '',
                updated_at: existingSubmission.submitted_at || '',
              } as FormSubmission);
            }
          })
          .catch(err => { if (!isSilentFetchError(err)) console.error('[WorkBreakdown] Failed to load submission:', err); });
      }
    } else {
      setSelectedFormSubmission(null);
      setShowFormModal(true);
    }
  }, [onFormSelect, workItemId, getAuthToken]);

  const handleInternalFormSubmit = useCallback(async (
    form: OperationalForm,
    formData: Record<string, unknown>,
    options?: {
      generatePdf?: boolean;
      status?: 'draft' | 'pass' | 'fail' | 'conditional';
      sectionResults?: FormSubmission['section_results'];
      signatureData?: Record<string, unknown>;
      gpsData?: { lat: number; lng: number; accuracy: number };
      existingSubmissionId?: number;
    }
  ): Promise<FormSubmission | null> => {
    try {
      setIsFormSubmitting(true);
      const token = getAuthToken();
      const targetWorkItemId = selectedFormSubmission?.work_item_id || workItemId;

      // Editing an existing saved submission → PATCH it instead of creating a duplicate
      if (options?.existingSubmissionId) {
        const patchRes = await fetch(getApiUrl(`/api/v1/forms/submissions/${options.existingSubmissionId}`), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            form_data: formData,
            overall_status: options?.status,
            section_results: options?.sectionResults,
            signature_data: options?.signatureData,
            gps_data: options?.gpsData,
          }),
        });

        if (patchRes.ok) {
          const data = await patchRes.json();
          if (data.success && data.data) {
            // Refetch immediately — the PATCH has already completed (we have
            // the response), so the server has the updated data. The previous
            // fixed 300ms setTimeout could race with server-side processing
            // or delay the UI update unnecessarily.
            loadDynamicForms().then(() => {
              if (parentJobId || workItemType === 'appointment') {
                loadInheritedForms();
              }
            });
            window.dispatchEvent(new CustomEvent('processChecklistUpdated'));
            return data.data;
          }
          throw new Error((data?.error as string) || (data?.message as string) || 'Update failed');
        }
        // Parse the error body so FORM_EDIT_WINDOW_CLOSED / permission messages
        // reach the user instead of a generic HTTP failure.
        const patchErrorData = await patchRes.json().catch(() => ({}));
        const patchErrMsg = (patchErrorData?.error as string) || (patchErrorData?.message as string) || `Update failed (HTTP ${patchRes.status})`;
        throw new Error(patchErrMsg);
      }

      const res = await fetch(getApiUrl('/api/v1/forms/submissions'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_configuration_id: form.id,
          form_key: form.form_key,
          work_item_id: targetWorkItemId,
          entity_type: form.entity_type || 'work_item',
          form_data: formData,
          overall_status: options?.status,
          section_results: options?.sectionResults,
          signature_data: options?.signatureData,
          gps_data: options?.gpsData,
          generate_pdf: options?.generatePdf ?? false,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          // Refresh forms & submissions after successful submit
          // Also refresh inherited forms so sibling appointments see the update
          setTimeout(() => {
            loadDynamicForms().then(() => {
              if (parentJobId || workItemType === 'appointment') {
                loadInheritedForms();
              }
            });
          }, 300);
          // Notify sidebar/process checklist that submissions changed
          window.dispatchEvent(new CustomEvent('processChecklistUpdated'));
          return data.data;
        }
        throw new Error((data?.error as string) || (data?.message as string) || 'Submission failed');
      }
      // Surface the error to the caller (OperationalFormModal will display it).
      // Always parse the body on failure — FORM_EDIT_WINDOW_CLOSED and other
      // server reasons must reach the user instead of a generic HTTP error.
      const errorData = await res.json().catch(() => ({}));
      const submitErrMsg = (errorData?.error as string) || (errorData?.message as string) || `Submission failed (HTTP ${res.status})`;
      throw new Error(submitErrMsg);
    } catch (err) {
      // Re-throw extracted API errors so the caller can display the message.
      // Only log and swallow unexpected (non-Error) failures.
      if (err instanceof Error) throw err;
      console.error('[WorkBreakdown] Form submission error:', err);
      return null;
    } finally {
      setIsFormSubmitting(false);
    }
  }, [selectedFormSubmission?.work_item_id, workItemId, loadDynamicForms, loadInheritedForms, parentJobId, workItemType, getAuthToken]);

  // ──────────────────────────────────────────────────────────────────────
  // Toggle a form on/off for this work item (admin feature)
  // ──────────────────────────────────────────────────────────────────────
  const handleToggleForm = async (formId: number) => {
    const isRemoving = assignedFormIds.includes(formId) || dynamicForms.some(f => f.id === formId);
    const isAutoForm = autoAssignedFormIds.includes(formId);
    const isInheritedForm = inheritedFormIds.includes(formId);

    // Compute new extra and removed lists
    let newExtras = [...extraFormIds];
    let newRemoved = [...removedAutoFormIds];

    if (isRemoving) {
      if (isAutoForm || isInheritedForm) {
        // Removing an auto or inherited form → add to removed list
        if (!newRemoved.includes(formId)) newRemoved.push(formId);
      } else {
        // Removing an admin-added extra → remove from extras list
        newExtras = newExtras.filter(id => id !== formId);
      }
    } else {
      if (isAutoForm || isInheritedForm) {
        // Re-adding a previously removed auto/inherited form → remove from removed list
        newRemoved = newRemoved.filter(id => id !== formId);
      } else {
        // Adding a new extra form
        if (!newExtras.includes(formId)) newExtras.push(formId);
      }
    }

    // Compute new display list
    const visibleAutoIds = autoAssignedFormIds.filter(id => !newRemoved.includes(id));
    const newIds = [...visibleAutoIds, ...newExtras.filter(id => !visibleAutoIds.includes(id))];

    // Optimistic update
    setExtraFormIds(newExtras);
    setRemovedAutoFormIds(newRemoved);
    setAssignedFormIds(newIds);
    const allFormsMap = new Map<number, DynamicFormInfo>();
    dynamicForms.forEach(f => allFormsMap.set(f.id, f));
    allActiveClientForms.forEach(f => { if (!allFormsMap.has(f.id)) allFormsMap.set(f.id, f); });
    // Include inherited forms in the map so removed inherited forms can be re-added
    inheritedForms.forEach(f => { if (!allFormsMap.has(f.id)) allFormsMap.set(f.id, f); });
    // Build display: assigned IDs + visible inherited (not in newRemoved)
    const directDisplayForms = newIds
      .map((id) => allFormsMap.get(id))
      .filter((form): form is DynamicFormInfo => Boolean(form))
      .map(applyRequirementToForm);
    const visibleInherited = inheritedForms
      .filter(f => !newRemoved.includes(f.id))
      .map(applyRequirementToForm);
    const displayForms = mergeFormsByFormKey(directDisplayForms, visibleInherited);
    setDynamicForms(displayForms);
    setDynamicFormsCount(displayForms.length);

    // Persist both lists
    setSavingFormAssignment(true);
    try {
      await persistFormAssignments(newExtras, newRemoved, assignmentRequirementsRef.current);
    } catch (error) {
      console.error('Error saving form assignment:', error);
    } finally {
      setSavingFormAssignment(false);
    }
  };

  const handleToggleFormRequirement = useCallback(async (
    formId: number,
    field: keyof FormAssignmentRequirement,
  ) => {
    const currentForm = dynamicForms.find((form) => form.id === formId)
      || inheritedForms.find((form) => form.id === formId)
      || allActiveClientForms.find((form) => form.id === formId)
      || null;
    const currentRequirement = getRequirementForForm(formId, currentForm);
    const nextRequirement: FormAssignmentRequirement = {
      ...currentRequirement,
      [field]: !currentRequirement[field],
    };
    const nextAssignmentRequirements = {
      ...assignmentRequirementsRef.current,
      [String(formId)]: nextRequirement,
    };

    setAssignmentRequirements(nextAssignmentRequirements);
    setDynamicForms((prev) => prev.map((form) => (
      form.id === formId
        ? { ...form, ...nextRequirement }
        : form
    )));
    setInheritedForms((prev) => prev.map((form) => (
      form.id === formId
        ? { ...form, ...nextRequirement }
        : form
    )));
    setAllActiveClientForms((prev) => prev.map((form) => (
      form.id === formId
        ? { ...form, ...nextRequirement }
        : form
    )));

    setSavingFormAssignment(true);
    try {
      await persistFormAssignments(extraFormIds, removedAutoFormIds, nextAssignmentRequirements);
    } catch (error) {
      console.error('Error saving form requirements:', error);
    } finally {
      setSavingFormAssignment(false);
    }
  }, [allActiveClientForms, dynamicForms, extraFormIds, getRequirementForForm, inheritedForms, persistFormAssignments, removedAutoFormIds]);

  // Forms currently displayed on this work item (used by the picker to show
  // which catalogue forms are already assigned).
  const currentlyDisplayedIds = new Set(dynamicForms.map(f => f.id));

  // Card search — filters the rendered forms list.
  const normalizedContentSearch = contentSearch.trim().toLowerCase();
  const matchesContentSearch = (...values: Array<string | null | undefined>) =>
    !normalizedContentSearch
    || values.some((value) => typeof value === 'string' && value.toLowerCase().includes(normalizedContentSearch));

  const filteredDisplayableForms = displayableForms.filter((form) =>
    matchesContentSearch(form.title, form.description, form.category, form.form_key));

  const workItemTypeLabel = workItemType === 'appointment'
    ? 'appointment'
    : workItemType === 'job' ? 'job' : 'work item';

  return (
    <div className="space-y-4 sm:space-y-6" data-tour-id="work-item-breakdown-tab">
      {/* Header — mirrors the Appointments tab layout */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Forms <UserDocsLink docsPath="/total-operations/workflows/service-operations/work-breakdown" label="User guide: Work breakdown" className="ml-1 align-middle" /></h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Operational forms assigned to this {workItemTypeLabel}
          </p>
        </div>
        {/* Add form + AI assistant — admin only, hidden in readOnly/technician mode */}
        {canManageForms && (
          <div className="flex items-center gap-2">
            {enableAiAssistant && (
              <button
                onClick={() => setShowAiAssistant((prev) => !prev)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showAiAssistant
                  ? 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                  : 'border-gray-300 bg-white text-amber-600 hover:bg-amber-50 dark:border-gray-700 dark:bg-gray-800 dark:text-amber-400 dark:hover:bg-amber-900/20'
                  }`}
                title="AI Forms Assistant — create a once-off form and link it to this job"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">AI Assistant</span>
              </button>
            )}
            <button
              data-tour-id="work-item-breakdown-add-form"
              type="button"
              onClick={() => setShowFormPicker(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Form
            </button>
          </div>
        )}
      </div>

      {/* Forms card — mirrors the Appointments tab grid card */}
      <div className="rounded-2xl border shadow-sm overflow-hidden border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="border-b px-4 py-3 border-gray-200 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-900/90">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                Form register
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Search the assigned forms — click a row to open it. Auto-added forms are linked to this {workItemTypeLabel} type or this customer/site.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="relative block min-w-[260px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="search"
                  value={contentSearch}
                  onChange={(event) => setContentSearch(event.target.value)}
                  placeholder="Search forms..."
                  className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
              </label>

              <div className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm border-gray-300 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <span className="font-medium">{filteredDisplayableForms.length}</span>
                <span>of {displayableForms.length} form{displayableForms.length === 1 ? '' : 's'}</span>
              </div>
            </div>
          </div>
        </div>

      {/* ─── FORMS GRID — appointment-style rows ──────────────────────────── */}
      <div className="overflow-x-auto" data-tour-id="work-item-breakdown-forms">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/80">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Form</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Requirement</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Last submission</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {loadingDynamicForms ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                </td>
              </tr>
            ) : displayableForms.length === 0 && !loadingInherited ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <FileText className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {shouldRestrictTerminalAppointmentForms ? 'No forms were completed for this appointment' : parentJobId ? 'No forms assigned to this appointment or parent job' : `No forms assigned to this ${workItemTypeLabel}`}
                  </p>
                  {canManageForms && (
                    <button
                      onClick={() => setShowFormPicker(true)}
                      className="mx-auto mt-3 flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add a form
                    </button>
                  )}
                </td>
              </tr>
            ) : filteredDisplayableForms.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <Search className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No forms matched your search</p>
                  <p className="mt-1 text-xs text-gray-500">Try another form name, category, or key.</p>
                </td>
              </tr>
            ) : filteredDisplayableForms.map(form => {
                    const submissions = formSubmissions.filter(s => s.form_key === form.form_key);
                    const currentSubmission = submissions.find(
                      (submission) => normalizeSubmissionWorkItemId(submission.work_item_id) === normalizeSubmissionWorkItemId(workItemId)
                    );
                    // Only use the current work item's submission — never fall
                    // back to a sibling appointment's submission. Previously
                    // `currentSubmission || submissions[0]` could show another
                    // appointment's status badge and target a foreign work_item_id.
                    const latestSubmission = currentSubmission || null;
                    const isAutoForm = autoAssignedFormIds.includes(form.id);
                    const isInherited = inheritedForms.some(f => f.form_key === form.form_key) && !formSubmissions.some(s => s.form_key === form.form_key && s.work_item_id === workItemId);

                    // Check if a sibling appointment already completed this form
                    const sibSubs = siblingSubmissions[form.form_key] || [];
                    const siblingCompleted = sibSubs.filter(s => !s.is_current_appointment && ['pass', 'submitted', 'approved'].includes(s.status));

                    const sectionCount = form.form_schema?.sections?.length || 0;
                    const fieldCount = form.form_schema?.sections?.reduce(
                      (sum: number, s: { fields?: Array<unknown> }) => sum + (s.fields?.length || 0), 0
                    ) || 0;
                    const isGeneratedVariant = isGeneratedOnceOffForm(form);
                    const generatedRepeatCounts = getGeneratedVariantRepeatCounts(form);
                    const generatedRepeatSummary = Object.entries(generatedRepeatCounts)
                      .map(([repeatGroupKey, count]) => {
                        const repeatGroupLabel = getRepeatGroupCatalog(form)[repeatGroupKey]?.label
                          || repeatGroupKey.replace(/[_-]/g, ' ');
                        return `${repeatGroupLabel}: ${count}`;
                      })
                      .join(' · ');
                    const sourceTemplateForm = form.source_form_id != null
                      ? allActiveClientForms.find((candidate) => candidate.id === Number(form.source_form_id))
                        || dynamicForms.find((candidate) => candidate.id === Number(form.source_form_id))
                      : null;
                    const explicitlyRequiredForJob = Boolean(form.required_for_job);
                    const explicitlyRequiredForAppointment = Boolean(form.required_for_appointment);
                    const isRequiredForScope = isOperationalFormRequired(form, requirementScope);
                    const complianceStandard = (form.compliance_meta as DynamicFormComplianceMeta | undefined)?.compliance_standard;

                    const statusColors: Record<string, { bg: string; text: string; label: string }> = {
                      pass: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'COMPLETED' },
                      submitted: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'SUBMITTED' },
                      draft: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', label: 'DRAFT' },
                      fail: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'FAILED' },
                      conditional: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', label: 'CONDITIONAL' },
                    };
                    const statusCfg = latestSubmission ? statusColors[latestSubmission.status] || statusColors.draft : null;
                    const canOpenForm = !effectiveFormsReadOnly
                      || isFormWithinEditableWindow(form)
                      || Boolean(latestSubmission);

                    // Editable window control — extracted so it can render both
                    // inside the full manage-controls row and standalone for
                    // admins on locked items (see below). It PATCHes the form
                    // config, not the work item, so it stays available even
                    // when the item's forms are locked.
                    const editableWindowControl = editingWindowFormId === form.id ? (
                      <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          min={0}
                          max={365}
                          value={windowDaysInput}
                          onChange={(e) => setWindowDaysInput(e.target.value)}
                          className="w-14 px-1.5 py-0.5 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-teal-400"
                          placeholder="0"
                          autoFocus
                        />
                        <span className="text-[11px] text-gray-500">days</span>
                        <button
                          onClick={() => void handleSaveEditableWindow(form)}
                          disabled={savingWindowDays}
                          className="px-2 py-0.5 text-[11px] font-semibold rounded-lg bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50"
                        >
                          {savingWindowDays ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingWindowFormId(null)}
                          className="px-1.5 py-0.5 text-[11px] font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingWindowFormId(form.id);
                          setWindowDaysInput(
                            form.compliance_meta?.editable_window_days
                              ? String(form.compliance_meta.editable_window_days)
                              : '0'
                          );
                        }}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${form.compliance_meta?.editable_window_days
                            ? 'bg-teal-100 border-teal-200 text-teal-700 dark:bg-teal-900/30 dark:border-teal-800 dark:text-teal-300'
                            : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-900/30 dark:border-gray-700 dark:text-gray-400'
                          }`}
                        title="Set how many days the technician can still edit this form after their appointment ends (0 = locked after appointment)"
                      >
                        {form.compliance_meta?.editable_window_days
                          ? `Editable ${form.compliance_meta.editable_window_days}d`
                          : 'Edit window'}
                      </button>
                    );

                    return (
                      <tr
                        key={form.id}
                        onClick={canOpenForm ? (e) => {
                          // Don't open the form if the click originated from an
                          // action button (remove/minus, requirement toggles,
                          // "Manage in Forms", etc.). stopPropagation on those
                          // buttons is the first line of defence, but this guard
                          // ensures the form never opens when a button is clicked.
                          if ((e.target as HTMLElement).closest('button')) return;
                          handleInternalFormSelect(form, latestSubmission || null);
                        } : undefined}
                        aria-disabled={!canOpenForm}
                        className={`transition-colors ${canOpenForm ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : 'cursor-not-allowed opacity-75'}`}
                      >
                        {/* Form — icon, title, badges, meta */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-start gap-2.5">
                            <div
                              className="p-1.5 rounded-lg flex-shrink-0"
                              style={{ backgroundColor: (form.color || '#f59e0b') + '15' }}
                            >
                              <FileText className="w-4 h-4" style={{ color: form.color || '#f59e0b' }} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">{form.title}</p>
                                {isRequiredForScope && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-bold flex-shrink-0">
                                    REQUIRED
                                  </span>
                                )}
                                {isAutoForm && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 font-medium flex-shrink-0">
                                    AUTO
                                  </span>
                                )}
                                {isGeneratedVariant && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 font-semibold flex-shrink-0">
                                    CUSTOM COUNT
                                  </span>
                                )}
                                {isInherited && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 font-medium flex-shrink-0">
                                    FROM JOB
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {sectionCount} section{sectionCount !== 1 ? 's' : ''} · {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                                </span>
                                {form.category && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                    {form.category}
                                  </span>
                                )}
                                {complianceStandard && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    Standard: {complianceStandard}
                                  </span>
                                )}
                              </div>
                              {generatedRepeatSummary && (
                                <p className="mt-1 text-xs text-violet-700 dark:text-violet-300">
                                  Exact counts: {generatedRepeatSummary}
                                </p>
                              )}
                              {form.description && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{form.description}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Checklist requirement — admin toggles or static badges */}
                        <td className="px-4 py-3 align-top">
                          {canManageForms ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleToggleFormRequirement(form.id, 'required_for_job');
                                }}
                                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${explicitlyRequiredForJob
                                    ? 'bg-blue-100 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300'
                                    : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-900/30 dark:border-gray-700 dark:text-gray-400'
                                  }`}
                                title="Toggle whether this form is required on the job"
                              >
                                Job
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleToggleFormRequirement(form.id, 'required_for_appointment');
                                }}
                                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${explicitlyRequiredForAppointment
                                    ? 'bg-purple-100 border-purple-200 text-purple-700 dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-300'
                                    : 'bg-white border-gray-200 text-gray-500 dark:bg-gray-900/30 dark:border-gray-700 dark:text-gray-400'
                                  }`}
                                title="Toggle whether this form is required on the appointment"
                              >
                                Appointment
                              </button>
                              {/* Editable window toggle — allows technician to edit
                                  the form for N days after appointment ends */}
                              {editableWindowControl}
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {explicitlyRequiredForJob && (
                                <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  Job
                                </span>
                              )}
                              {explicitlyRequiredForAppointment && (
                                <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  Appointment
                                </span>
                              )}
                              {!explicitlyRequiredForJob && !explicitlyRequiredForAppointment && (
                                <span className="text-xs text-gray-400 dark:text-gray-500">Optional</span>
                              )}
                              {/* Editable window control for admins even when the
                                  item's forms are locked (resolved/scheduled
                                  appointments, view mode) — the window setting lives
                                  on the form config, not the work item. */}
                              {isAdmin && !technicianMode && editableWindowControl}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 align-top">
                          <div className="space-y-1.5">
                            {statusCfg ? (
                              <span className={`inline-flex px-2 py-1 text-[11px] font-semibold rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
                                {statusCfg.label}
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-[11px] font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                NOT STARTED
                              </span>
                            )}
                            <div className={`text-xs font-medium ${canOpenForm ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {latestSubmission
                                ? 'Click to view'
                                : siblingCompleted.length > 0
                                  ? 'Completed by a sibling appointment'
                                  : canOpenForm
                                    ? 'Click to fill out this form'
                                    : workItemType === 'appointment'
                                      ? form.compliance_meta?.editable_window_days
                                        ? `Available once active (editable ${form.compliance_meta.editable_window_days}d after)`
                                        : 'Available once this appointment is active'
                                      : 'Available once this work item is active'}
                            </div>
                          </div>
                        </td>

                        {/* Last submission */}
                        <td className="px-4 py-3 align-top">
                          <div className="space-y-1.5 text-xs">
                            {latestSubmission && (
                              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                <Clock className="w-3 h-3 flex-shrink-0" />
                                <span>
                                  {latestSubmission.submitted_at
                                    ? new Date(latestSubmission.submitted_at).toLocaleDateString('en-ZA', {
                                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })
                                    : 'Draft'}
                                  {latestSubmission.submitted_by_name && ` by ${latestSubmission.submitted_by_name}`}
                                </span>
                              </div>
                            )}
                            {submissions.length > 1 && (
                              <div className="text-gray-400 dark:text-gray-500">
                                {submissions.length} submission{submissions.length > 1 ? 's' : ''}
                              </div>
                            )}
                            {/* Which appointment completed this form (job view) */}
                            {latestSubmission?.is_child_submission && latestSubmission.source_appointment_number && (
                              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                <span>
                                  Completed by{' '}
                                  <AppointmentReferenceBadge
                                    appointmentId={latestSubmission.work_item_id}
                                    appointmentNumber={latestSubmission.source_appointment_number}
                                    onOpen={onOpenItem}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                                    iconClassName="w-3 h-3"
                                    title={`Open appointment ${latestSubmission.source_appointment_number}`}
                                  >
                                    <>
                                      <span>{latestSubmission.source_appointment_number}</span>
                                    </>
                                  </AppointmentReferenceBadge>
                                  {latestSubmission.source_appointment_title && (
                                    <span className="text-gray-400 dark:text-gray-500"> ({latestSubmission.source_appointment_title})</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {/* Sibling appointment completion info (for inherited forms) */}
                            {siblingCompleted.map(sib => (
                              <div key={sib.submission_id} className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                <span>
                                  Completed by{' '}
                                  <AppointmentReferenceBadge
                                    appointmentId={sib.appointment_id}
                                    appointmentNumber={sib.appointment_number}
                                    onOpen={onOpenItem}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                                    iconClassName="w-3 h-3"
                                    title={`Open appointment ${sib.appointment_number}`}
                                  >
                                    <>
                                      <span>{sib.appointment_number}</span>
                                    </>
                                  </AppointmentReferenceBadge>
                                  {sib.submitted_at && (
                                    <> on {new Date(sib.submitted_at).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                                  )}
                                  {sib.submitted_by_name && <> by {sib.submitted_by_name}</>}
                                </span>
                              </div>
                            ))}
                            {!latestSubmission && siblingCompleted.length === 0 && (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex justify-end items-center gap-2">
                            {canManageForms && isGeneratedVariant && sourceTemplateForm && (
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openFormsGenerator(sourceTemplateForm);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border border-violet-200 text-violet-700 dark:border-violet-800 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                                title="Open the Forms dashboard to reconfigure the exact repeat counts for this once-off form"
                              >
                                Manage
                              </button>
                            )}
                            {canOpenForm && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInternalFormSelect(form, latestSubmission || null);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                                Open
                              </button>
                            )}
                            {/* Admin: edit form in UnifiedDesigner */}
                            {canManageForms && !formsReadOnly && (
                              <button
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditingFormKey(form.form_key); setFormBuilderOpen(true); }}
                                className="p-1.5 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 text-gray-400 hover:text-teal-600 transition-colors"
                                title="Edit form in builder"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {/* Admin: remove form button (hidden in readOnly mode) */}
                            {canManageForms && (
                              <button
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleToggleForm(form.id); }}
                                className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove form from this job"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
          </tbody>
        </table>
      </div>

      {/* Required forms summary warning */}
      {(() => {
        const requiredForms = displayableForms.filter(f =>
          isOperationalFormRequired(f, requirementScope)
        );
        const requiredNotSubmitted = requiredForms.filter(f => {
          // Check local submissions (including child appointment submissions for jobs)
          const sub = formSubmissions.find(s => s.form_key === f.form_key && ['pass', 'submitted', 'approved'].includes(s.status));
          if (sub) return false;
          // Also check sibling submissions (for appointments inheriting from jobs)
          const sibSubs = siblingSubmissions[f.form_key] || [];
          const sibCompleted = sibSubs.some(s => ['pass', 'submitted', 'approved'].includes(s.status));
          return !sibCompleted;
        });
        if (requiredForms.length > 0 && requiredNotSubmitted.length > 0) {
          return (
            <div className="mx-4 mb-4 flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
              <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                {requiredNotSubmitted.length} of {requiredForms.length} required form{requiredForms.length !== 1 ? 's are' : ' is'} not yet submitted
              </span>
            </div>
          );
        }
        return null;
      })()}

      {(savingFormAssignment || loadingInherited) && (
        <div className="px-4 pb-4 flex items-center gap-3 text-xs text-gray-400">
          {savingFormAssignment && (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          {loadingInherited && (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading inherited forms...
            </span>
          )}
        </div>
      )}
      </div>

      {/* Form catalogue picker (opened by "+ New Form") — admin-side attach
          only. canManageForms excludes technician/read-only contexts, so the
          picker can never open for field workers even if opened
          programmatically. */}
      {canManageForms && (
      <FormPickerModal
        isOpen={showFormPicker}
        onClose={() => setShowFormPicker(false)}
        forms={allActiveClientForms}
        assignedFormIds={currentlyDisplayedIds}
        autoAssignedFormIds={new Set(autoAssignedFormIds)}
        workItemSubtype={workItemSubtype}
        workItemTypeLabel={workItemTypeLabel}
        onToggleForm={(formId) => { void handleToggleForm(formId); }}
        onGenerateVariant={(form) => openFormsGenerator(form)}
      />
      )}

      {/* Built-in Operational Form Modal (when no external onFormSelect handler) */}
      {!onFormSelect && selectedForm && (
        <OperationalFormModal
          isOpen={showFormModal}
          onClose={() => { setShowFormModal(false); setSelectedForm(null); setSelectedFormSubmission(null); }}
          form={selectedForm}
          workItemId={workItemId}
          existingSubmission={selectedFormSubmission}
          onSubmit={handleInternalFormSubmit}
          isSubmitting={isFormSubmitting}
          readOnly={effectiveFormsReadOnly && !isFormWithinEditableWindow(selectedForm)}
          canEditExistingSubmission={!effectiveFormsReadOnly || isFormWithinEditableWindow(selectedForm)}
          initialData={builtInModalPrefill}
        />
      )}

      {/* ── AI Forms Assistant (floating panel, admin-only) ── */}
      {enableAiAssistant && canManageForms && showAiAssistant && (
        <React.Suspense fallback={<div className="flex items-center justify-center h-full"><BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} /></div>}>
          <AIFormsAssistant
            floatingPanel
            onClose={() => setShowAiAssistant(false)}
            onEditForm={(formKey) => {
              // AI created/edited a form — link it to this work item, then open it in the designer
              setShowAiAssistant(false);
              void linkAiCreatedFormToWorkItem(formKey).then(() => {
                setEditingFormKey(formKey);
                setFormBuilderOpen(true);
              });
            }}
          />
        </React.Suspense>
      )}

      {/* ── Unified Form Designer (opened from edit button on form rows) ── */}
      {formBuilderOpen && (
        <React.Suspense fallback={<div className="flex items-center justify-center p-12"><BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} /></div>}>
          <UnifiedDesigner
            type="form"
            documentId={editingFormKey}
            onClose={() => { setFormBuilderOpen(false); setEditingFormKey(undefined); }}
            onSaved={() => {
              // Reload forms to reflect any changes made in the editor
              loadDynamicForms();
              if (parentJobId || workItemType === 'appointment') {
                loadInheritedForms();
              }
            }}
          />
        </React.Suspense>
      )}
    </div>
  );
}
